'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from 'primereact/button';
import { UploadSection }          from './UploadSection';
import { WarrantyResultCard }     from './WarrantyResultCard';
import { checkWarrantyByInvoice } from '../_helpers';
import { OrderTicketInfoPanel }   from '@/app/support/_components/OrderTicketInfoPanel';

/* ─── Auth helper (cookie-based, client-only) ───────────────────────────── */
const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

// Inline style (not className) so it wins over PrimeReact's own .p-button
// padding/font-size rules regardless of CSS load order.
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const update = () => setIsMobile(window.innerWidth < 768);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return isMobile;
};

/* ─── Official-website CTA card ─────────────────────────────────────────── */
const OfficialWebsiteCard = () => {
    const router   = useRouter();
    const [mounted,    setMounted]    = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        setIsLoggedIn(!!getAuthUser());
        setMounted(true);
    }, []);

    const handleClick = () => {
        if (isLoggedIn) {
            router.push('/account/orders');
        } else {
            router.push('/login?redirect=/account/orders');
        }
    };

    return (
        <div
            className="flex flex-column gap-3 border-round-xl p-4"
            style={{
                background: 'linear-gradient(135deg, rgba(27,184,167,0.08), rgba(99,102,241,0.08))',
                border: '1.5px solid rgba(27,184,167,0.25)',
            }}
        >
            {/* Mobile: row 1 = title/badge (full width), row 2 = button (full width).
                Desktop: title/badge on the left, button on the right. */}
            <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                <div className="flex align-items-center gap-3 w-full md:w-auto" style={{ flex: 1 }}>
                    {/* Icon */}
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width: 38, height: 38,
                            background: 'linear-gradient(135deg, #1bb8a7, #6366f1)',
                            boxShadow: '0 3px 12px rgba(27,184,167,0.35)',
                        }}
                    >
                        <i className="pi pi-shopping-bag" style={{ fontSize: '1rem', color: '#fff' }} />
                    </div>

                    {/* Title + domain badge */}
                    <div className="flex flex-column md:flex-row md:align-items-center gap-1 md:gap-2" style={{ flex: 1 }}>
                        <p className="m-0 font-bold text-900" style={{ fontSize: '0.95rem' }}>
                            Bought from our official website?
                        </p>
                        <span
                            style={{
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                background: '#f0fdf4',
                                color: '#16a34a',
                                border: '1px solid #16a34a30',
                                borderRadius: '999px',
                                padding: '0.15rem 0.5rem',
                                whiteSpace: 'nowrap',
                                width: 'fit-content',
                            }}
                        >
                            ennixelectronics.com
                        </span>
                    </div>
                </div>

                {/* CTA button */}
                {mounted && (
                    <Button
                        label={isLoggedIn ? 'Go to My Orders' : 'Login to view orders'}
                        icon={isLoggedIn ? 'pi pi-shopping-bag' : 'pi pi-sign-in'}
                        className={isMobile ? 'w-full' : 'w-auto'}
                        style={{
                            background: 'var(--primary-color)',
                            border: 'none',
                            borderRadius: '0.65rem',
                            fontWeight: 700,
                            fontSize: isMobile ? '1rem' : '0.82rem',
                            padding: isMobile ? '0.9rem 1.25rem' : '0.55rem 1.25rem',
                            maxWidth: isMobile ? undefined : '180px',
                            boxShadow: '0 3px 12px rgba(11,49,99,0.3)',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                        onClick={handleClick}
                    />
                )}
            </div>

            {/* ── Row 2: raise a ticket from your order ── */}
            <OrderTicketInfoPanel wrapStyle={{ margin: 0 }} light />
        </div>
    );
};

/* ─── Divider with label ─────────────────────────────────────────────────── */
const OrDivider = () => (
    <div
        className="flex align-items-center gap-3 my-4"
        style={{ maxWidth: '560px', margin: '1.25rem auto' }}
    >
        <div style={{ flex: 1, height: 1, background: 'var(--surface-border)' }} />
        <span
            style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#9ca3af',
                background: 'var(--surface-card)',
                padding: '0.2rem 0.75rem',
                border: '1px solid var(--surface-border)',
                borderRadius: '999px',
                whiteSpace: 'nowrap',
            }}
        >
            OR — upload invoice below
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--surface-border)' }} />
    </div>
);

/* ─── Result Summary Bar ─────────────────────────────────────────────────── */
const ResultSummary = ({ data }) => {
    const inWarranty  = data.filter(d => d.warranty?.status === 'in_warranty').length;
    const outWarranty = data.filter(d => d.warranty?.status === 'out_of_warranty').length;

    return (
        <div
            className="flex align-items-center justify-content-between gap-3 flex-wrap border-round-xl p-3 mb-4"
            style={{
                background: 'var(--surface-card)',
                border: '1.5px solid var(--surface-border)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}
        >
            <div className="flex align-items-center gap-2">
                <i className="pi pi-shield" style={{ fontSize: '0.9rem', color: '#10b981' }} />
                <span className="font-bold" style={{ fontSize: '0.9rem', color: 'var(--text-color)' }}>
                    {data.length} product{data.length !== 1 ? 's' : ''} found
                </span>
            </div>
            <div className="flex align-items-center gap-2 flex-wrap">
                {inWarranty > 0 && (
                    <span
                        className="flex align-items-center gap-1 font-bold border-round-3xl"
                        style={{
                            background: '#dcfce7', color: '#16a34a',
                            border: '1px solid #16a34a30',
                            fontSize: '0.72rem', padding: '0.25rem 0.7rem',
                        }}
                    >
                        <i className="pi pi-check-circle" style={{ fontSize: '0.65rem' }} />
                        {inWarranty} in warranty
                    </span>
                )}
                {outWarranty > 0 && (
                    <span
                        className="flex align-items-center gap-1 font-bold border-round-3xl"
                        style={{
                            background: '#fee2e2', color: '#dc2626',
                            border: '1px solid #dc262630',
                            fontSize: '0.72rem', padding: '0.25rem 0.7rem',
                        }}
                    >
                        <i className="pi pi-times-circle" style={{ fontSize: '0.65rem' }} />
                        {outWarranty} out of warranty
                    </span>
                )}
            </div>
        </div>
    );
};

/* ─── No-results state ───────────────────────────────────────────────────── */
const NoResults = () => (
    <div
        className="flex flex-column align-items-center gap-3 text-center border-round-xl p-5"
        style={{ background: 'var(--surface-card)', border: '1.5px dashed var(--surface-border)' }}
    >
        <div
            className="flex align-items-center justify-content-center border-circle"
            style={{
                width: 56, height: 56,
                background: 'rgba(245,158,11,0.1)',
                border: '1.5px solid rgba(245,158,11,0.3)',
            }}
        >
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.4rem', color: '#f59e0b' }} />
        </div>
        <div>
            <p className="m-0 font-bold" style={{ fontSize: '1rem', color: 'var(--text-color)' }}>
                No valid Ennix invoice found
            </p>
            <p className="m-0 mt-2 text-500" style={{ fontSize: '0.82rem', maxWidth: '360px' }}>
                Make sure you are uploading an original invoice PDF from Amazon or Flipkart for an Ennix product.
            </p>
            <p
                className="m-0 mt-2 font-semibold"
                style={{ fontSize: '0.8rem', maxWidth: '360px', color: '#f59e0b' }}
            >
                Please upload the original PDF file, or check the status again.
            </p>
        </div>
        <a
            href="/contactus"
            style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                color: '#f59e0b', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none',
            }}
        >
            Need help? Contact support <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
        </a>
    </div>
);

/* ─── Main Page Component ────────────────────────────────────────────────── */
export const WarrantySupportPage = () => {
    const router = useRouter();
    const [file,    setFile]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [apiErr,  setApiErr]  = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;

        setLoading(true);
        setApiErr('');
        setResults(null);

        const res = await checkWarrantyByInvoice(file);
        setLoading(false);

        if (!res.success) {
            if (res.message === 'No valid invoice found in the PDF') {
                setResults([]);
            } else {
                setApiErr(res.message || 'Something went wrong. Please try again.');
            }
            return;
        }

        setResults(res.data || []);
    };

    return (
        <main style={{ minHeight: '100vh', background: 'var(--surface-ground)' }}>

            {/* ── Hero Header ──────────────────────────────────────────────── */}
            <div
                style={{
                    background: 'var(--primary-color)',
                    padding: '2.5rem 1rem 2.75rem',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div style={{ position: 'relative', maxWidth: 890, margin: '0 auto', padding: '0 2rem' }}>

                    {/* ── Back button ── */}
                    <button
                        onClick={() => router.push('/support')}
                        style={{
                            display:        'inline-flex',
                            alignItems:     'center',
                            gap:            '0.55rem',
                            padding:        '0.65rem 1.5rem',
                            borderRadius:   '0.75rem',
                            background:     'rgba(255,255,255,0.12)',
                            border:         '1.5px solid rgba(255,255,255,0.22)',
                            color:          '#fff',
                            fontSize:       '0.9rem',
                            fontWeight:     700,
                            cursor:         'pointer',
                            backdropFilter: 'blur(6px)',
                            marginBottom:   '1.5rem',
                            transition:     'background 0.2s, border-color 0.2s',
                            letterSpacing:  '0.01em',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
                    >
                        <i className="pi pi-arrow-left" style={{ fontSize: '0.85rem' }} />
                        Back to Support
                    </button>

                    {/* Title row */}
                    <div className="flex align-items-center gap-3 mb-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{
                                width: 56, height: 56,
                                background: 'rgba(255,255,255,0.15)',
                                border: '1.5px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            <i className="pi pi-shield" style={{ fontSize: '1.5rem', color: '#fff' }} />
                        </div>
                        <div>
                            <h1 className="m-0 font-bold" style={{ fontSize: '1.6rem', color: '#fff', lineHeight: 1.2 }}>
                                Warranty Check
                            </h1>
                            <p className="m-0 mt-1" style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.88rem' }}>
                                Upload your invoice PDF for instant warranty status
                            </p>
                        </div>
                    </div>

                    {/* Feature chips */}
                    <div className="flex align-items-center gap-2 flex-wrap mb-4">
                        {[
                            { icon: 'pi pi-bolt',   label: 'Instant results'   },
                            { icon: 'pi pi-lock',   label: 'Secure & private'  },
                            { icon: 'pi pi-amazon', label: 'Amazon & Flipkart' },
                        ].map(chip => (
                            <span
                                key={chip.label}
                                className="flex align-items-center gap-1"
                                style={{
                                    background: '#fff',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    borderRadius: '999px',
                                    padding: '0.25rem 0.7rem',
                                    fontSize: '0.72rem',
                                    color: 'var(--primary-color)',
                                    fontWeight: 600,
                                }}
                            >
                                <i className={chip.icon} style={{ fontSize: '0.65rem', color: 'var(--primary-color)' }} />
                                {chip.label}
                            </span>
                        ))}
                    </div>

                </div>
            </div>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <section style={{ maxWidth: 890, margin: '0 auto', padding: '2.5rem 2rem' }}>

                {/* ── Official-website CTA card ─ */}
                <OfficialWebsiteCard />

                {/* ── OR divider ── */}
                <OrDivider />

                {/* ── Upload card ── */}
                <form id="warranty-upload-form" onSubmit={handleSubmit}>
                    <div
                        className="border-round-xl p-4 mb-4"
                        style={{
                            background: 'var(--surface-card)',
                            border: '1.5px solid var(--surface-border)',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        }}
                    >
                        {/* Card header */}
                        <div className="flex align-items-center gap-2 mb-4">
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{
                                    width: 36, height: 36,
                                    background: 'rgba(16,185,129,0.12)',
                                    border: '1.5px solid rgba(16,185,129,0.3)',
                                }}
                            >
                                <i className="pi pi-upload" style={{ fontSize: '0.9rem', color: '#10b981' }} />
                            </div>
                            <div>
                                <p className="m-0 font-bold" style={{ fontSize: '0.95rem', color: 'var(--text-color)' }}>
                                    Upload Invoice
                                </p>
                                <p className="m-0 text-500" style={{ fontSize: '0.75rem' }}>
                                    Only PDF format — Amazon or Flipkart invoices
                                </p>
                            </div>
                        </div>

                        <UploadSection
                            onFileSelect={setFile}
                            loading={loading}
                        />
                    </div>
                </form>

                {/* API error */}
                {apiErr && (
                    <div
                        className="flex align-items-center gap-2 border-round-lg px-4 py-3 mb-4"
                        style={{
                            background: '#fee2e2',
                            border: '1px solid #ef444430',
                            color: '#dc2626',
                            fontSize: '0.85rem',
                        }}
                    >
                        <i className="pi pi-exclamation-circle" style={{ fontSize: '1rem', flexShrink: 0 }} />
                        <span>{apiErr}</span>
                    </div>
                )}

                {/* ── Results ──────────────────────────────────────────────── */}
                {results !== null && (
                    <section>
                        <div className="flex align-items-center gap-3 mb-4">
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{
                                    width: 36, height: 36,
                                    background: 'rgba(16,185,129,0.12)',
                                    border: '1.5px solid rgba(16,185,129,0.3)',
                                }}
                            >
                                <i className="pi pi-list" style={{ fontSize: '0.9rem', color: '#10b981' }} />
                            </div>
                            <h2 className="m-0 font-bold" style={{ fontSize: '1.1rem', color: 'var(--text-color)' }}>
                                Warranty Results
                            </h2>
                        </div>

                        {results.length === 0 ? (
                            <NoResults />
                        ) : (
                            <>
                                <ResultSummary data={results} />
                                <div className="flex flex-column gap-3">
                                    {results.map((item, i) => (
                                        <WarrantyResultCard key={i} item={item} index={i} invoiceFile={file} />
                                    ))}
                                </div>
                            </>
                        )}
                    </section>
                )}

                {/* ── Already raised a ticket? Track it ────────────────── */}
                <div
                    className="flex align-items-center justify-content-between gap-3 flex-wrap border-round-xl p-4 mt-5"
                    style={{
                        background: 'linear-gradient(135deg, rgba(27,184,167,0.07), rgba(99,102,241,0.06))',
                        border: '1.5px solid rgba(27,184,167,0.22)',
                    }}
                >
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{ width: 36, height: 36, background: 'rgba(27,184,167,0.12)', border: '1.5px solid rgba(27,184,167,0.3)' }}
                        >
                            <i className="pi pi-search" style={{ fontSize: '0.9rem', color: '#1bb8a7' }} />
                        </div>
                        <div>
                            <p className="m-0 font-bold" style={{ fontSize: '0.9rem', color: 'var(--text-color)' }}>
                                Already raised a repair ticket?
                            </p>
                            <p className="m-0 mt-1" style={{ fontSize: '0.76rem', color: 'var(--text-color-secondary, #64748b)' }}>
                                Check the live status of your repair using your ticket number, email, or mobile.
                            </p>
                        </div>
                    </div>
                    <a
                        href="/track-status"
                        style={{
                            display:      'inline-flex',
                            alignItems:   'center',
                            gap:          '0.4rem',
                            padding:      '0.55rem 1.25rem',
                            borderRadius: '0.65rem',
                            background:   'var(--primary-color)',
                            color:        '#fff',
                            fontWeight:   700,
                            fontSize:     '0.82rem',
                            textDecoration: 'none',
                            boxShadow:    '0 3px 12px rgba(27,184,167,0.35)',
                            whiteSpace:   'nowrap',
                            flexShrink:   0,
                        }}
                    >
                        <i className="pi pi-search" style={{ fontSize: '0.72rem' }} />
                        Track Repair Status
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                    </a>
                </div>

                {/* ── How it works note ────────────────────────────────────── */}
                <div
                    className="flex align-items-start gap-3 border-round-xl p-4 mt-3"
                    style={{
                        background: 'rgba(99,102,241,0.05)',
                        border: '1.5px solid rgba(99,102,241,0.15)',
                    }}
                >
                    <i className="pi pi-info-circle" style={{ fontSize: '1.1rem', color: '#6366f1', marginTop: '0.1rem', flexShrink: 0 }} />
                    <div>
                        <p className="m-0 font-bold" style={{ fontSize: '0.88rem', color: 'var(--text-color)' }}>
                            How does this work?
                        </p>
                        <p className="m-0 mt-1 text-600" style={{ fontSize: '0.8rem', lineHeight: 1.7 }}>
                            We scan your invoice PDF (Amazon/Flipkart) for Ennix product details and calculate
                            warranty status based on the invoice date. Standard warranty is <strong>6 months</strong> from purchase.
                            Your file is never stored — it is processed in real-time and discarded immediately.
                        </p>
                        <div className="flex gap-2 flex-wrap mt-2">
                            <a
                                href="/contactus"
                                className="flex align-items-center gap-1 no-underline font-bold"
                                style={{ color: '#6366f1', fontSize: '0.8rem' }}
                            >
                                <i className="pi pi-send" style={{ fontSize: '0.65rem' }} />
                                Contact support
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};
